/* eslint-disable react-hooks/exhaustive-deps */
import DocumentOnlyViewer from "components/DocumentOnlyViewer";
import Loading from "components/Loading";
import PageHeader from "components/PageHeader";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBriefcase } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  CaseListParams,
  fetchCaseCreation,
  fetchCaseCreationByCoordinatorWithType,
  fetchCaseCreationByProsecutorId,
  fetchCaseCreationByRegistrarWithType,
} from "services/caseServices";
import { CaseCreation, CaseStatus, CaseStatusFilter, FILE_URL, Roles } from "types";
import { eyeIcon, fileIcon } from "utils/constant";
import { DateView, errorHandler, hasRole } from "utils/helper";

// Status-based filter tabs. `field` says which case field to match against:
// "status" (CaseStatus) or "status_filter" (CaseStatusFilter — court stage).
const STATUS_FILTERS: {
  label: string;
  value: string | null;
  field?: "status" | "status_filter";
}[] = [
  { label: "All", value: null },
  {
    label: CaseStatus.forwarded_to_coordinator,
    value: "forwarded_to_coordinator",
    field: "status",
  },
  {
    label: CaseStatus.forwarded_to_registrar,
    value: "forwarded_to_registrar",
    field: "status",
  },
  { label: CaseStatus.sent_to_court, value: "sent_to_court", field: "status" },
  {
    label: CaseStatusFilter.final_verdict_given,
    value: "final_verdict_given",
    field: "status_filter",
  },
  {
    label: CaseStatusFilter.appealed,
    value: "appealed",
    field: "status_filter",
  },
  {
    label: CaseStatus.case_terminated_by_court,
    value: "case_terminated_by_court",
    field: "status",
  },
];
const tabs = STATUS_FILTERS.map((f) => f.label);
const ITEMS_PER_PAGE = 9;

const InstituteOfChargeListForSidebar = () => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const navigate = useNavigate();

  const canViewFile = hasRole(loginData?.roles, [
    Roles.director,
    Roles.registrar,
    Roles.coordinator,
    Roles.generalAttorney,
  ]);

  const [activeTab, setActiveTab] = useState("All");
  const [cases, setCases] = useState<CaseCreation[]>([]);
  const [loading, setLoading] = useState(true);

  const isVarious = hasRole(loginData?.roles, [
    Roles.variousCoordinator,
    Roles.variousDirector,
    Roles.variousRegistrar,
  ]);
  const isEconomic = hasRole(loginData?.roles, [
    Roles.economicCoordinator,
    Roles.economicDirector,
  ]);
  const isZonalVariousChildRegistrar = hasRole(loginData?.roles, [
    Roles.zonalVariousChildWomenRegistrar,
  ]);
  const isZonalEconomicCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.zonalEconomicCoruptionRegistrsrar,
  ]);

  const isVariousRegistrar = hasRole(loginData?.roles, [
    Roles.variousRegistrar,
  ]);
  const isEconomicRegistrar = hasRole(loginData?.roles, [
    Roles.economicRegistrar,
  ]);
  const isCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.corruptionRegistrar,
  ]);

  const isVariousDirector = hasRole(loginData?.roles, [Roles.variousDirector]);
  const isEconomicDirector = hasRole(loginData?.roles, [Roles.economicDirector]);
  const isCorruptionDirector = hasRole(loginData?.roles, [
    Roles.corruptionDirector,
  ]);
  // NewCorruptionCaseCoordinator — fetch only their crime-type's cases via the
  // by-coordinator-role endpoint (was previously falling through to all cases).
  const isCorruptionCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionCoordinator,
  ]);

  const isChildWomen = hasRole(loginData?.roles, [Roles.childWomenCoordinator]);
  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  // Clerk lists charges using the coordinator's by-coordinator-role filter.
  const isClerk = hasRole(loginData?.roles, [Roles.clerk]);
  // Crime-type clerks fetch their coordinator's charges via the same
  // by-coordinator-role endpoint, scoped to their crime type's coordinator.
  const isVariousClerk = hasRole(loginData?.roles, [Roles.variousClerk]);
  const isEconomicClerk = hasRole(loginData?.roles, [Roles.economicClerk]);
  const isCorruptionClerk = hasRole(loginData?.roles, [Roles.corruptionClerk]);
  const isChildWomenClerk = hasRole(loginData?.roles, [Roles.childWomenClerk]);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);

  // Map the active status tab to the right backend filter param (status vs
  // status_filter). The "on Court Hearing" tab is served separately below.
  const activeFilter = STATUS_FILTERS.find((f) => f.label === activeTab);
  const statusParam =
    activeFilter?.field === "status"
      ? activeFilter.value || undefined
      : undefined;
  const statusFilterParam =
    activeFilter?.field === "status_filter"
      ? activeFilter.value || undefined
      : undefined;

  const fetchData = (page = currentPage) => {
    setLoading(true);

    const adminUnit = loginData?.admin_unit || "";
    const orgUnit = loginData?.org_unit || "";
    const params: CaseListParams = {
      page,
      pageSize: ITEMS_PER_PAGE,
      search: debouncedSearch || undefined,
      status: statusParam,
      status_filter: statusFilterParam,
    };
    const byCoordinator = (role: string) =>
      fetchCaseCreationByCoordinatorWithType(adminUnit, orgUnit, role, params);
    const byRegistrar = (role: string) =>
      fetchCaseCreationByRegistrarWithType(adminUnit, role, params);

    const api =
      isZonalVariousChildRegistrar || isZonalEconomicCorruptionRegistrar
        ? byCoordinator(
            isZonalVariousChildRegistrar
              ? Roles.zonalVariousChildWomenRegistrar
              : Roles.zonalEconomicCoruptionRegistrsrar,
          )
        : isVariousDirector
          ? byCoordinator(Roles.variousDirector)
          : isEconomicDirector
            ? byCoordinator(Roles.economicDirector)
            : isCorruptionDirector
              ? byCoordinator(Roles.corruptionDirector)
              : isVarious
                ? byCoordinator(Roles.variousCoordinator)
                : isEconomic
                  ? byCoordinator(Roles.economicCoordinator)
                  : isCorruptionCoordinator
                    ? byCoordinator(Roles.corruptionCoordinator)
                    : isChildWomen
                      ? byCoordinator(Roles.childWomenCoordinator)
                      : isVariousClerk
                      ? byCoordinator(Roles.variousClerk)
                      : isEconomicClerk
                      ? byCoordinator(Roles.economicClerk)
                      : isCorruptionClerk
                      ? byCoordinator(Roles.corruptionClerk)
                      : isChildWomenClerk
                      ? byCoordinator(Roles.childWomenClerk)
                      : isVariousRegistrar
                      ? byRegistrar(Roles.variousRegistrar)
                      : isEconomicRegistrar
                        ? byRegistrar(Roles.economicRegistrar)
                        : isCorruptionRegistrar
                          ? byRegistrar(Roles.corruptionCoordinator)
                          : isClerk
                            ? byCoordinator(Roles.coordinator)
                            : isProsecutor
                            ? fetchCaseCreationByProsecutorId(
                                loginData?.user_id || "",
                                params,
                              )
                            : fetchCaseCreation(params);

    api
      .then((data) => {
        setCases(data.results || []);
        setCount(data.count || 0);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setCases([]);
        setCount(0);
        setLoading(false);
      });
  };

  // Debounce the search box; reset to the first page on a new term.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Refetch the case list on login / page / search / status-tab changes.
  useEffect(() => {
    if (loginData) fetchData(currentPage);
  }, [loginData, currentPage, debouncedSearch, activeTab]);

  const totalPages = Math.ceil(count / ITEMS_PER_PAGE);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("charge_list")}
        icon={FaBriefcase}
        tabs={tabs}
        activeTab={activeTab}
        onTabClick={(tab: string) => {
          setActiveTab(tab);
          setCurrentPage(1);
        }}
        rightContent={<></>}
      />

      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {loading ? (
            <Loading label={t("loading_charges")} />
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="text-white bg-black">
                <tr>
                  <th colSpan={8} className="p-4" rounded-t-lg>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <button
                          onClick={() => navigate(-1)}
                          className="text-white hover:text-gray-300"
                          title={t("go_back")}
                        >
                          ◀️
                        </button>
                        <span>{t("charge_list")}</span>
                      </div>

                      <div className="relative w-80">
                        <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                          🔍
                        </span>
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder={t("search")}
                          className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th className="p-3 text-left">
                    {t("prosecutor_registration_number")}
                  </th>
                  <th className="p-3 text-left">{t("date_sent")}</th>
                  <th className="p-3 text-left">
                    {t("estimated_finish_date")}
                  </th>
                  <th className="p-3 text-left">{t("status")}</th>
                  <th className="p-3 text-left" rounded-tr-lg>
                    {t("view")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {cases.map((charge, idx) => (
                  <tr
                    key={charge?.id ?? idx}
                    className={idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"}
                  >
                    <td className="p-3 font-[500] font-[Poppins]">
                      {charge.prosecutor_registration_number}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {DateView(charge?.date_sent_to_prosecutor)}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {DateView(charge?.estimated_day_to_finish)}
                    </td>
                    <td className="p-3 font-[500] font-[inter]">
                      <StatusBadge
                        status={
                          // During a court update, the case re-enters the
                          // workflow — show the real case status. Otherwise show
                          // the court stage (status_filter) when set, falling
                          // back to the case's own status. The opening-file
                          // status is internal and not shown here.
                          charge?.is_case_updated_by_court
                            ? charge?.status || ""
                            : charge?.status_filter &&
                              charge?.status_filter !== "on_charge_draft"
                            ? charge.status_filter
                            : charge?.status || ""
                        }
                      />
                    </td>
                    <td className="p-3 font-[500] font-[Poppins] flex flex-row items-center gap-4">
                      <div
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => {
                          navigate(charge?.id?.toString(), {
                            state: charge,
                          });
                        }}
                      >
                        {eyeIcon}
                        {/* Show file icon if charge has attached files */}
                      </div>
                      {Array.isArray(charge?.documents) &&
                        charge.documents.some(
                          (doc) => doc.document_type === "upload_charge_draft",
                        ) && (
                          <DocumentOnlyViewer
                            file={charge.documents[0]}
                            baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.CASE_CREATION}`}
                            actionTrigger={
                              <span
                                title="Has attached files"
                                className="text-xl text-blue-500"
                              >
                                {fileIcon}
                              </span>
                            }
                          />
                        )}
                    </td>
                  </tr>
                ))}
                {!loading && cases.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-4 text-center text-gray-500">
                      {t("no_charges_found")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              {t("prev")}
            </button>
            <span className="px-2">
              {t("page")} {currentPage} {t("of")} {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              {t("next")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation();
  status = status.toLowerCase();
  const getStatusColor = (status: string) => {
    const statusMap: { [key: string]: string } = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      approved: "bg-green-100 text-green-800 border-green-200",
      respond_back_cordinator: "bg-blue-100 text-blue-800 border-blue-200",
      investigation_recorded: "bg-gray-100 text-gray-800 border-gray-200",
      respond_back_prosecutor:
        "bg-purple-100 text-purple-800 border-purple-200",
      forwarded_to_director: "bg-pink-100 text-pink-800 border-pink-200",
      forwarded_to_registrar: "bg-orange-100 text-orange-800 border-orange-200",
      sent_to_court: "bg-green-100 text-green-800 border-green-200",
      on_panel: "bg-blue-100 text-blue-800 border-blue-200",
      case_terminated_by_court: "bg-red-100 text-red-800 border-red-200",
      on_charge_draft: "bg-gray-100 text-gray-800 border-gray-200",
      on_court_hearing: "bg-blue-100 text-blue-800 border-blue-200",
      final_verdict_given: "bg-green-100 text-green-800 border-green-200",
      appealed: "bg-purple-100 text-purple-800 border-purple-200",
      submitted: "bg-yellow-100 text-yellow-800 border-yellow-200",
      assigned_to_prosecutor: "bg-red-100 text-red-800 border-red-200",
      assigned_to_clerk: "bg-blue-100 text-blue-800 border-blue-200",
      forwarded_to_coordinator:
        "bg-indigo-100 text-indigo-800 border-indigo-200",
      completed: "bg-green-100 text-green-800 border-green-200",
    };
    return statusMap[status] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const label = t(`status_${status}`) || status;

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
        status,
      )}`}
    >
      {label}
    </span>
  );
};

export default InstituteOfChargeListForSidebar;
